export const DEFAULT_LOGGER_PIXEL_URL = "https://prod.log.shortbread.aws.dev/1x1.png";
export const DEFAULT_CONSOLE_INTEGRATION_LOGGER_PIXEL_URL = "https://prod.log.shortbread.console.api.aws/1x1.png";
export const DEFAULT_CONSOLE_INTEGRATION_FALLBACK_LOGGER_PIXEL_URL = "https://prod.log.shortbread.analytics.console.aws.a2z.com/1x1.png";
const DEFAULT_TIMEOUT_MILLIS = 5000;
const LOGGER_VERSION = "1";
const READY_STATE_DONE = 4;

interface LoggerOptions {
    baseUrl?: string,
    timeout?: number,
    backoffDelays?: number[],
    onSuccess?: (url?: string) => void,
    onFail?: () => void,
    onError?: (status?: string) => void,
}
export interface Log {
    (severity: Severity): (message: string, payload?: object) => void
}
export type Severity = "error" | "warning" | "info";
const loggerHttpRequest = (url: string, timeout: number, onError?: () => void, onSuccess?: () => void) => {
    try {
        const xhr = new XMLHttpRequest();
        xhr.onreadystatechange = () => {
            if (xhr.readyState === READY_STATE_DONE) {
                if (xhr.status >= 200 && xhr.status < 300) {
                    if (onSuccess) onSuccess();
                } else {
                    if (onError) onError();
                }
            }
        }
        xhr.ontimeout = () => {
            if (onError) onError();
        }
        xhr.onerror = () => {
            if (onError) onError();
        }
        xhr.open("HEAD", url);
        // setting the timeout after the .open() to fix InvalidStateError on IE 11
        // based on the suggestion here: https://github.com/stephanebachelier/superapi/issues/5
        xhr.timeout = timeout;
        xhr.send();
    } catch (e) {

        if (onError) onError();
    }
}

const makeUrlString = (baseUrl: string, severity, message: string, payload = {}):string => {
    const payloadData = {
        timestamp: Date.now(),
        logVersion: LOGGER_VERSION,
        domain: window.location.host,
        url: window.location.href,
        ...payload
    }
    // JSON.stringify will omit all object attributes that are undefined (as desired)
    const paramString = `severity=${encodeURIComponent(severity)}&message=${encodeURIComponent(message)}&payload=${encodeURIComponent(JSON.stringify(payloadData))}`
    return baseUrl + "?" + paramString;
}

// url into the log
export const createLogger = (options: LoggerOptions): Log => {
    const {
        baseUrl = DEFAULT_LOGGER_PIXEL_URL,
        timeout = DEFAULT_TIMEOUT_MILLIS,
        onFail,
        onSuccess
    } = options
    return (severity) => (message, payload = {}) => {
        const url = makeUrlString(baseUrl, severity, message, payload);
        loggerHttpRequest(url, timeout, 
            // On failure of primary domain
            () => {
                // Try fallback domain
                if (baseUrl === DEFAULT_LOGGER_PIXEL_URL) {
                    // No need to retry in non-console apps
                    return;
                }

                // Only retry in the console. Some customers have a network allowlist that blocks traffic to unknown domains. 
                // Since we're introducing a new domain and customers haven't yet added console.api.aws to their allowlists, we need a fallback mechanism to ensure continued service functionality
                // (Project Wall Garden)
                const fallbackUrl = makeUrlString(DEFAULT_CONSOLE_INTEGRATION_FALLBACK_LOGGER_PIXEL_URL, severity, message, payload);

                loggerHttpRequest(fallbackUrl, timeout, 
                    // On failure of fallback domain
                    () => {
                        if (onFail) onFail();
                    },
                    // On success of fallback domain
                    () => {
                        if (onSuccess) onSuccess(fallbackUrl);
                    }
                );
            },
            // On success of primary domain
            () => {
                if (onSuccess) onSuccess(url);
            }
        );
    };
}

export const DEFAULT_LOGGER = createLogger({baseUrl: DEFAULT_LOGGER_PIXEL_URL});
export default createLogger
